import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { withSkipSpriGlobalLoader } from '@spri/services/loading';
import { TenantContextService } from 'app/core/services/tenant-context.util';
import { AutomatedSurveyService } from 'app/modules/admin/patient-surveys/automated/form/form.service';
import type {
  ApiResponse,
  AppointmentFilterOptionsResponse,
} from 'app/modules/admin/patient-surveys/shared/interfaces/common.interface';
import {
  FormTemplate,
  FormTemplatesResponse,
} from 'app/modules/admin/patient-surveys/shared/interfaces/patient-survey.interface';
import { environment } from 'environments/environment';
import { defer, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

export interface ManualFormSelectOption {
  value: string;
  label: string;
  /** Present for clinical manual options (template id for POST …/clinician-survey/manual). */
  formTemplateId?: string;
}

/**
 * lk_form_template_type.code values for clinical manual flyout lists (same codes as DB).
 */
const MANUAL_CLINICAL_SURGERY_TYPE_CODES = new Set(['surgery']);
const MANUAL_CLINICAL_EXAM_TYPE_CODES = new Set(['clinic_exam', 'pt_exam']);

/** Surgery manual: dropdown shows joint types. Exam manual: dropdown shows distinct form template titles. */
type ManualClinicalOptionLabelSource = 'joint' | 'template';

function toSelectOptions(items: { value: string | number; label: string }[]): ManualFormSelectOption[] {
  return items.map((o) => ({ value: String(o.value), label: o.label }));
}

function buildManualClinicalOptions(
  templates: FormTemplate[],
  allowedTypeCodes: ReadonlySet<string>,
  labelSource: ManualClinicalOptionLabelSource
): ManualFormSelectOption[] {
  const rows: ManualFormSelectOption[] = [];
  for (const template of templates) {
    if (!template.isActive) {
      continue;
    }
    const url = template.url?.trim();
    if (!url) {
      continue;
    }
    const typeCode = template.formTemplateType?.code?.trim().toLowerCase();
    if (!typeCode || !allowedTypeCodes.has(typeCode)) {
      continue;
    }
    let label = '';
    if (labelSource === 'joint') {
      label = template.jointType?.name?.trim() || template.jointType?.code?.trim() || '';
      if (!label) {
        continue;
      }
    } else {
      label = (template.name?.trim() || template.code || 'Form').trim();
    }
    rows.push({ value: url, label, formTemplateId: template.id });
  }
  return rows.sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }));
}

@Injectable({ providedIn: 'root' })
export class ManualFormService {
  private readonly patientApiUrl = environment.api.microservices.patient;
  private readonly surveyApiUrl = environment.api.microservices.survey;

  private readonly tenantContext = inject(TenantContextService);
  private readonly automatedSurveyService = inject(AutomatedSurveyService);
  private readonly http = inject(HttpClient);

  private readonly surveyOptions$ = this.automatedSurveyService.loadAllOptions().pipe(
    map(() => ({
      joints: toSelectOptions(this.automatedSurveyService.getJointsSync()),
      categories: toSelectOptions(this.automatedSurveyService.getCategoriesSync()),
    })),
    catchError(() =>
      of({
        joints: [] as ManualFormSelectOption[],
        categories: [] as ManualFormSelectOption[],
      })
    ),
    shareReplay(1)
  );

  readonly jointsOptions$ = this.surveyOptions$.pipe(map((o) => o.joints));

  readonly categoryOptions$ = this.surveyOptions$.pipe(map((o) => o.categories));

  private readonly clinicalTemplateOptions$ = defer(() => {
    const tenantId = this.tenantContext.getTenantId();
    return this.http
      .get<
        ApiResponse<FormTemplatesResponse>
      >(`${this.surveyApiUrl}/v1/tenant/${tenantId}/form-templates`, { context: withSkipSpriGlobalLoader() })
      .pipe(
        map((response) => response.data?.response ?? []),
        catchError(() => of<FormTemplate[]>([]))
      );
  }).pipe(shareReplay(1));

  readonly clinicalSurgeryJointOptions$ = this.clinicalTemplateOptions$.pipe(
    map((templates) => buildManualClinicalOptions(templates, MANUAL_CLINICAL_SURGERY_TYPE_CODES, 'joint'))
  );

  readonly clinicalExamJointOptions$ = this.clinicalTemplateOptions$.pipe(
    map((templates) => buildManualClinicalOptions(templates, MANUAL_CLINICAL_EXAM_TYPE_CODES, 'template'))
  );

  readonly physicianOptions$ = defer(() => {
    const tenantId = this.tenantContext.getTenantId();
    return this.http
      .get<
        ApiResponse<AppointmentFilterOptionsResponse>
      >(`${this.patientApiUrl}/v1/tenant/${tenantId}/patients/filter-options`, { context: withSkipSpriGlobalLoader() })
      .pipe(
        map((response) => {
          const options = response.data.physicians.map((physician) => ({
            value: physician.id,
            label: physician.name,
          }));
          return [...options].sort((leftOption, rightOption) =>
            leftOption.label.localeCompare(rightOption.label, undefined, { sensitivity: 'base' })
          );
        }),
        catchError(() => of<ManualFormSelectOption[]>([]))
      );
  }).pipe(shareReplay(1));
}
